/* Pohjoisen Rent – oma varauskalenteri (korvaa izyRentin)
 * Kaikki ajat käsitellään kaupan paikallisena "seinäkelloaikana" (Europe/Helsinki),
 * riippumatta asiakkaan selaimen aikavyöhykkeestä.
 * Hinnoittelu: jokainen alkava vuorokausi = kalenteripäivä noutopäivästä palautuspäivään.
 * Noudolla ja palautuksella on omat aikaikkunat (oletus nouto 09–16, palautus 09–19). */
(() => {
  const MIN_DAY = 24 * 60;
  const pad = (n) => String(n).padStart(2, '0');

  class PrBooking extends HTMLElement {
    connectedCallback() {
      try {
        this.data = JSON.parse(this.querySelector('script[type="application/json"]').textContent);
      } catch (e) {
        console.error('pr-booking: data missing', e);
        return;
      }
      const d = this.data;
      this.tz = d.settings.timezone || 'Europe/Helsinki';
      this.t = d.i18n;
      this.locale = d.settings.locale || 'fi';
      this.currency = d.settings.currency || 'EUR';
      this.capacity = Math.max(1, d.capacity || 1);
      this.buffer = (d.bufferHours || 0) * 60;
      this.minDays = Math.max(1, d.minDays || 1);
      this.maxDays = d.maxDays || 0;
      this.baseLevel = d.baseLevel;
      this.variants = {};
      d.variants.forEach((v) => { this.variants[v.level] = v; });
      if (!this.variants[this.baseLevel] && d.variants.length) this.baseLevel = d.variants[0].level;
      this.periods = d.periods.map((p) => ({ ...p, s: this.parseWall(p.s), e: this.parseWall(p.e) })).filter((p) => p.s !== null && p.e !== null);
      this.busy = [];
      /* Varausrivit: TILAUS;ALKU;LOPPU;MÄÄRÄ – erottimena | (Shopify Flow) tai rivinvaihto (käsin) */
      (d.bookings || []).map(String).join('\n').split(/[|\n\r]+/).forEach((line) => {
        line = String(line).trim();
        if (!line) return;
        const parts = line.split(';').map((x) => x.trim());
        if (parts.length < 3) return;
        const s = this.parseWall(parts[1]); const e = this.parseWall(parts[2]);
        if (s === null || e === null) return;
        this.busy.push({ n: parts[0], s, e: e + this.buffer, q: parseInt(parts[3] || '1', 10) || 1 });
      });
      (d.blocks || []).forEach((b) => {
        const s = this.parseWall(b.s); const e = this.parseWall(b.e);
        if (s === null || e === null) return;
        this.busy.push({ n: b.n, s, e: e + this.buffer, q: b.q || 1 });
      });
      const now = this.instantToWall(new Date());
      this.earliest = now + (d.settings.leadHours || 0) * 60;
      this.today = this.dayStart(now);
      this.lastDay = this.today + (d.settings.maxDaysAhead || 365) * MIN_DAY;
      const s = d.settings;
      this.pickupTimes = this.buildTimes(s.pickupFrom || '09:00', s.pickupTo || '16:00');
      this.returnTimes = this.buildTimes(s.returnFrom || '09:00', s.returnTo || '19:00');
      /* Palveluikkuna = aikaisimmasta noudosta/palautuksesta viimeisimpään. Sen mukaan päivä näytetään vapaana, osittain varattuna tai varattuna. */
      this.winStart = Math.min(this.pickupTimes[0], this.returnTimes[0]);
      this.winEnd = Math.max(this.pickupTimes[this.pickupTimes.length - 1], this.returnTimes[this.returnTimes.length - 1], this.winStart + 1);
      this.view = this.dayStart(Math.max(this.today, this.earliest));
      this.view = this.monthStart(this.view);
      this.sel = { start: null, end: null, startTime: null, endTime: null };
      this.render();
    }

    /* ---------- aika-apurit ---------- */
    parseWall(s) {
      if (s === null || s === undefined) return null;
      s = String(s).trim();
      const m = s.match(/^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2})(?::(\d{2}))?(?:\.\d+)?(Z|[+-]\d{2}:?\d{2})?)?$/);
      if (!m) return null;
      if (m[7]) return this.instantToWall(new Date(s));
      return Date.UTC(+m[1], +m[2] - 1, +m[3], +(m[4] || 0), +(m[5] || 0)) / 60000;
    }
    instantToWall(date) {
      const parts = new Intl.DateTimeFormat('en-GB', { timeZone: this.tz, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).formatToParts(date);
      const o = {}; parts.forEach((p) => { o[p.type] = p.value; });
      return Date.UTC(+o.year, +o.month - 1, +o.day, +o.hour % 24, +o.minute) / 60000;
    }
    wallDate(w) { return new Date(w * 60000); }
    dayStart(w) { return Math.floor(w / MIN_DAY) * MIN_DAY; }
    monthStart(w) { const d = this.wallDate(w); return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1) / 60000; }
    addMonths(w, n) { const d = this.wallDate(w); return Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + n, 1) / 60000; }
    weekday(w) { return (this.wallDate(w).getUTCDay() + 6) % 7; }
    /* Alkavat vuorokaudet = kalenteripäivät noutopäivästä palautuspäivään, molemmat mukaan lukien */
    calDays(start, end) { return Math.floor((this.dayStart(end) - this.dayStart(start)) / MIN_DAY) + 1; }
    isoWall(w) { const d = this.wallDate(w); return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}T${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}`; }
    fmtDate(w) { const d = this.wallDate(w); return `${d.getUTCDate()}.${d.getUTCMonth() + 1}.${d.getUTCFullYear()}`; }
    fmtShort(w) { const d = this.wallDate(w); return `${d.getUTCDate()}.${d.getUTCMonth() + 1}.`; }
    fmtTime(w) { const d = this.wallDate(w); return `${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}`; }
    fmtMin(m) { return `${pad(Math.floor(m / 60))}:${pad(m % 60)}`; }
    fmtMonth(w) { return new Intl.DateTimeFormat(this.locale, { month: 'long', year: 'numeric', timeZone: 'UTC' }).format(this.wallDate(w)); }
    weekdayNames() {
      const f = new Intl.DateTimeFormat(this.locale, { weekday: 'short', timeZone: 'UTC' });
      const base = Date.UTC(2024, 0, 1); // maanantai
      return [0, 1, 2, 3, 4, 5, 6].map((i) => f.format(new Date(base + i * 864e5)).replace('.', ''));
    }
    money(cents) {
      const whole = cents % 100 === 0;
      return new Intl.NumberFormat(this.locale, { style: 'currency', currency: this.currency, minimumFractionDigits: whole ? 0 : 2, maximumFractionDigits: 2 }).format(cents / 100);
    }
    buildTimes(from, to) {
      const [fh, fm] = String(from).split(':').map(Number);
      const [th, tm] = String(to).split(':').map(Number);
      const step = Math.max(15, this.data.settings.step || 60);
      const first = fh * 60 + (fm || 0), last = th * 60 + (tm || 0);
      const out = [];
      for (let t = first; t <= last; t += step) out.push(t);
      if (!out.length) out.push(first);
      return out;
    }

    /* ---------- hinnat ---------- */
    levelFor(day) {
      const names = ['ma', 'ti', 'ke', 'to', 'pe', 'la', 'su'];
      const wd = names[this.weekday(day)];
      let best = null;
      for (const p of this.periods) {
        if (day < p.s || day > p.e) continue;
        if (p.days && p.days.length && !p.days.includes(wd)) continue;
        if (!best) { best = p; continue; }
        const pr = p.prio || 0, br = best.prio || 0;
        if (pr > br) { best = p; continue; }
        if (pr < br) continue;
        if (p.specific && !best.specific) { best = p; continue; }
        if (p.specific === best.specific && (p.e - p.s) < (best.e - best.s)) best = p;
      }
      const name = best ? best.level : this.baseLevel;
      return this.variants[name] || this.variants[this.baseLevel];
    }
    /* Jokainen kalenteripäivä hinnoitellaan oman päivänsä hintatasolla */
    quote(start, end) {
      const days = Math.max(1, this.calDays(start, end));
      const lines = new Map();
      let total = 0;
      const d0 = this.dayStart(start);
      for (let i = 0; i < days; i++) {
        const day = d0 + i * MIN_DAY;
        const v = this.levelFor(day);
        if (!v) continue;
        if (!lines.has(v.id)) lines.set(v.id, { variant: v, qty: 0, dates: [] });
        const l = lines.get(v.id);
        l.qty += 1; l.dates.push(day);
        total += v.price;
      }
      return { days, lines: [...lines.values()], total };
    }

    /* ---------- saatavuus ---------- */
    usage(s, e) {
      // palauttaa {min,max} samanaikaisesta käytöstä välillä [s,e)
      const evs = [];
      for (const b of this.busy) {
        if (b.s < e && b.e > s) evs.push([Math.max(b.s, s), b.q], [Math.min(b.e, e), -b.q]);
      }
      if (!evs.length) return { min: 0, max: 0 };
      evs.sort((a, b) => a[0] - b[0] || a[1] - b[1]);
      let cur = 0, max = 0, min = Infinity, t = s;
      for (const [time, q] of evs) {
        if (time > t) { min = Math.min(min, cur); t = time; }
        cur += q; max = Math.max(max, cur);
      }
      if (t < e) min = Math.min(min, cur);
      if (min === Infinity) min = 0;
      return { min, max };
    }
    isFree(s, e) { return this.usage(s, e + this.buffer).max + 1 <= this.capacity; }
    /* Päivän tila katsotaan palveluikkunan (esim. 09–19) sisällä: koko ikkuna varattu = varattu, osa siitä = osittain varattu */
    dayStatus(day) {
      if (day < this.today || day > this.lastDay) return 'past';
      /* Päivä, jolle ei ehdi enää noutamaan (varausaika ennen noutoa), ei ole valittavissa */
      if (day + this.pickupTimes[this.pickupTimes.length - 1] < this.earliest) return 'past';
      const u = this.usage(day + this.winStart, day + this.winEnd);
      if (u.min >= this.capacity) return 'full';
      if (u.max >= this.capacity) return 'partial';
      return 'free';
    }

    /* ---------- render ---------- */
    render() {
      const t = this.t;
      const minPrice = Math.min(...Object.values(this.variants).map((v) => v.price));
      const hint = (t.time_hint || '')
        .replace('{a}', this.fmtMin(this.pickupTimes[0])).replace('{b}', this.fmtMin(this.pickupTimes[this.pickupTimes.length - 1]))
        .replace('{c}', this.fmtMin(this.returnTimes[0])).replace('{d}', this.fmtMin(this.returnTimes[this.returnTimes.length - 1]));
      this.innerHTML = `
        <div class="prb">
          <div class="prb__top">
            <div class="prb__price"><span class="prb__from">${t.from}</span> <b>${this.money(minPrice)}</b> <span class="prb__unit">${t.per_day}</span></div>
            <div class="prb__legend"><span class="prb__lg prb__lg--free">${t.free}</span><span class="prb__lg prb__lg--partial">${t.partial}</span><span class="prb__lg prb__lg--full">${t.booked}</span></div>
          </div>
          <div class="prb__cal" data-cal></div>
          <div class="prb__times">
            <label class="prb__field"><span>${t.pickup}</span><span class="prb__fieldval" data-start-label>${t.choose_date}</span><select data-start-time aria-label="${t.pickup_time}" disabled></select></label>
            <label class="prb__field"><span>${t.return}</span><span class="prb__fieldval" data-end-label>${t.choose_date}</span><select data-end-time aria-label="${t.return_time}" disabled></select></label>
          </div>
          ${hint ? `<p class="prb__timehint">${hint}</p>` : ''}
          <p class="prb__msg" data-msg hidden></p>
          <fieldset class="prb__group" data-omavastuu>
            <legend>${t.deductible}</legend>
            ${this.renderOmavastuu()}
          </fieldset>
          ${this.data.addons.length ? `<fieldset class="prb__group" data-addons><legend>${t.addons}</legend>${this.data.addons.map((a) => `
            <label class="prb__opt"><input type="checkbox" value="${a.id}" data-addon> ${a.image ? `<img src="${a.image}" alt="" loading="lazy">` : ''}<span class="prb__optname">${this.esc(a.title)}</span><span class="prb__optprice">+ ${this.money(a.price)}</span></label>`).join('')}</fieldset>` : ''}
          <div class="prb__summary" data-summary></div>
          <button type="button" class="button prb__btn" data-add disabled>${t.add_to_cart}</button>
          <p class="prb__note">${t.note}</p>
        </div>`;
      this.$cal = this.querySelector('[data-cal]');
      this.$startTime = this.querySelector('[data-start-time]');
      this.$endTime = this.querySelector('[data-end-time]');
      this.$msg = this.querySelector('[data-msg]');
      this.$summary = this.querySelector('[data-summary]');
      this.$btn = this.querySelector('[data-add]');
      this.$cal.addEventListener('click', (e) => this.onCalClick(e));
      this.$startTime.addEventListener('change', () => { this.sel.startTime = +this.$startTime.value; this.update(); });
      this.$endTime.addEventListener('change', () => { this.sel.endTime = +this.$endTime.value; this.update(); });
      this.querySelectorAll('[data-addon], [name="prb-omavastuu"]').forEach((el) => el.addEventListener('change', () => this.update()));
      this.$btn.addEventListener('click', () => this.addToCart());
      this.renderCal();
      this.update();
    }
    renderOmavastuu() {
      const o = this.data.omavastuu;
      if (!o.default && !o.reduced) return '';
      const t = this.t;
      let html = '';
      if (o.default) html += `<label class="prb__opt"><input type="radio" name="prb-omavastuu" value="${o.default.id}" checked><span class="prb__optname">${this.esc(o.default.title)}</span><span class="prb__optprice">${o.default.price ? '+ ' + this.money(o.default.price) : t.included}</span></label>`;
      if (o.reduced) html += `<label class="prb__opt"><input type="radio" name="prb-omavastuu" value="${o.reduced.id}" ${o.default ? '' : 'checked'}><span class="prb__optname">${this.esc(o.reduced.title)}</span><span class="prb__optprice">${o.reduced.price ? '+ ' + this.money(o.reduced.price) : t.included}</span></label>`;
      return html;
    }
    esc(s) { return String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c])); }

    renderCal() {
      const months = window.matchMedia('(min-width: 750px)').matches ? 2 : 1;
      const wds = this.weekdayNames();
      const show = this.data.settings.showPrices;
      let html = `<div class="prb__nav"><button type="button" class="prb__navbtn" data-nav="-1" aria-label="${this.t.prev_month}">&lsaquo;</button><button type="button" class="prb__navbtn" data-nav="1" aria-label="${this.t.next_month}">&rsaquo;</button></div><div class="prb__months">`;
      for (let m = 0; m < months; m++) {
        const ms = this.addMonths(this.view, m);
        const me = this.addMonths(ms, 1);
        html += `<div class="prb__month"><div class="prb__mtitle">${this.fmtMonth(ms)}</div><div class="prb__grid">`;
        html += wds.map((w) => `<span class="prb__wd">${w}</span>`).join('');
        const lead = this.weekday(ms);
        for (let i = 0; i < lead; i++) html += '<span class="prb__pad"></span>';
        for (let day = ms; day < me; day += MIN_DAY) {
          const st = this.dayStatus(day);
          const cls = ['prb__day', `prb__day--${st}`];
          if (this.sel.start !== null && day === this.sel.start) cls.push('prb__day--start');
          if (this.sel.end !== null && day === this.sel.end) cls.push('prb__day--end');
          if (this.sel.start !== null && this.sel.end !== null && day > this.sel.start && day < this.sel.end) cls.push('prb__day--in');
          const v = this.levelFor(day);
          const price = show && v ? `<small>${Math.round(v.price / 100)}</small>` : '';
          const disabled = st === 'past' || st === 'full' ? 'disabled' : '';
          html += `<button type="button" class="${cls.join(' ')}" data-day="${day}" ${disabled}><span>${this.wallDate(day).getUTCDate()}</span>${price}</button>`;
        }
        html += '</div></div>';
      }
      html += '</div>';
      this.$cal.innerHTML = html;
      this.$cal.querySelectorAll('[data-nav]').forEach((b) => b.addEventListener('click', () => { this.view = this.addMonths(this.view, +b.dataset.nav); this.renderCal(); }));
    }
    onCalClick(e) {
      const btn = e.target.closest('[data-day]');
      if (!btn || btn.disabled) return;
      const day = +btn.dataset.day;
      const s = this.sel;
      if (s.start === null || (s.start !== null && s.end !== null) || day < s.start) {
        s.start = day; s.end = null; s.startTime = null; s.endTime = null;
      } else {
        s.end = day; s.endTime = null;
      }
      this.renderCal();
      this.update();
    }
    fillTimes(select, options, current) {
      select.innerHTML = options.map((o) => `<option value="${o.value}" ${o.value === current ? 'selected' : ''}>${o.label}</option>`).join('');
      select.disabled = options.length === 0;
      return options.length ? (options.some((o) => o.value === current) ? current : options[0].value) : null;
    }
    update() {
      const t = this.t; const s = this.sel;
      this.querySelector('[data-start-label]').textContent = s.start !== null ? this.fmtDate(s.start) : t.choose_date;
      this.querySelector('[data-end-label]').textContent = s.end !== null ? this.fmtDate(s.end) : t.choose_date;
      let msg = '';
      // noutoajat nouto-ikkunasta
      if (s.start !== null) {
        const opts = this.pickupTimes.filter((m) => { const w = s.start + m; return w >= this.earliest && this.usage(w, w + 1).max < this.capacity; }).map((m) => ({ value: m, label: this.fmtTime(s.start + m) }));
        s.startTime = this.fillTimes(this.$startTime, opts, s.startTime);
        if (!opts.length) msg = t.no_pickup_times;
      } else { this.fillTimes(this.$startTime, [], null); }
      // palautusajat palautusikkunasta; vuorokaudet = kalenteripäivät
      if (s.start !== null && s.end !== null && s.startTime !== null) {
        const start = s.start + s.startTime;
        const days = this.calDays(s.start, s.end);
        const opts = this.returnTimes.filter((m) => {
          const end = s.end + m;
          if (end <= start) return false;
          if (days < this.minDays) return false;
          if (this.maxDays && days > this.maxDays) return false;
          return this.isFree(start, end);
        }).map((m) => ({ value: m, label: this.fmtTime(s.end + m) }));
        s.endTime = this.fillTimes(this.$endTime, opts, s.endTime);
        if (!opts.length) {
          if (days < this.minDays) msg = t.min_days.replace('{n}', this.minDays);
          else if (this.maxDays && days > this.maxDays) msg = t.max_days.replace('{n}', this.maxDays);
          else msg = t.not_available;
        }
      } else { this.fillTimes(this.$endTime, [], null); }
      this.$msg.hidden = !msg; this.$msg.textContent = msg;
      const ok = s.start !== null && s.end !== null && s.startTime !== null && s.endTime !== null && !msg;
      this.$btn.disabled = !ok;
      this.renderSummary(ok);
    }
    extras() {
      const out = [];
      const om = this.querySelector('[name="prb-omavastuu"]:checked');
      if (om) { const o = this.data.omavastuu; const item = [o.default, o.reduced].find((x) => x && String(x.id) === om.value); if (item) out.push({ ...item, kind: 'omavastuu' }); }
      this.querySelectorAll('[data-addon]:checked').forEach((cb) => { const a = this.data.addons.find((x) => String(x.id) === cb.value); if (a) out.push({ ...a, kind: 'addon' }); });
      return out;
    }
    renderSummary(ok) {
      const t = this.t;
      if (!ok) { this.$summary.innerHTML = `<p class="prb__hint">${this.sel.start === null ? t.choose_dates : t.choose_return}</p>`; return; }
      const start = this.sel.start + this.sel.startTime, end = this.sel.end + this.sel.endTime;
      const q = this.quote(start, end);
      const extras = this.extras();
      let total = q.total;
      let rows = q.lines.map((l) => `<div class="prb__row"><span>${l.qty} × ${this.money(l.variant.price)} <small>${this.esc(l.variant.level)}</small></span><b>${this.money(l.qty * l.variant.price)}</b></div>`).join('');
      extras.forEach((x) => { total += x.price; if (x.price) rows += `<div class="prb__row"><span>${this.esc(x.title)}</span><b>${this.money(x.price)}</b></div>`; });
      this.$summary.innerHTML = `<div class="prb__sumhead"><span>${this.fmtDate(start)} ${this.fmtTime(start)} → ${this.fmtDate(end)} ${this.fmtTime(end)}</span><b>${q.days} ${t.days}</b></div>${rows}<div class="prb__row prb__row--total"><span>${t.total}</span><b>${this.money(total)}</b></div>`;
    }
    async addToCart() {
      const t = this.t;
      const start = this.sel.start + this.sel.startTime, end = this.sel.end + this.sel.endTime;
      if (!this.isFree(start, end)) { this.$msg.hidden = false; this.$msg.textContent = t.not_available; return; }
      const q = this.quote(start, end);
      const key = `${this.data.productId}-${Date.now().toString(36)}`;
      const range = `${this.fmtDate(start)} ${t.at} ${this.fmtTime(start)} → ${this.fmtDate(end)} ${t.at} ${this.fmtTime(end)}`;
      const items = q.lines.map((l, i) => {
        const props = {};
        props[t.p_pickup] = `${this.fmtDate(start)} ${t.at} ${this.fmtTime(start)}`;
        props[t.p_return] = `${this.fmtDate(end)} ${t.at} ${this.fmtTime(end)}`;
        props[t.p_days] = l.dates.map((d) => this.fmtShort(d)).join(', ');
        if (i === 0) props._rent = `${this.isoWall(start)};${this.isoWall(end)};1`;
        else props._rent_part = `${i + 1}/${q.lines.length}`;
        props._rent_key = key;
        return { id: l.variant.id, quantity: l.qty, properties: props };
      });
      this.extras().forEach((x) => {
        const props = {}; props[t.p_device] = this.data.title; props[t.p_rental] = range; props._rent_key = key;
        items.push({ id: x.id, quantity: 1, properties: props });
      });
      items.reverse(); // Shopify näyttää viimeksi lisätyn ensin -> vuokrarivi ylimmäksi
      this.$btn.disabled = true; this.$btn.textContent = t.adding;
      try {
        const res = await fetch(this.data.settings.cartAddUrl || '/cart/add.js', { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ items }) });
        const json = await res.json();
        if (!res.ok || json.status) throw new Error(json.description || json.message || 'cart error');
        window.location.href = this.data.settings.cartUrl || '/cart';
      } catch (err) {
        console.error(err);
        this.$msg.hidden = false; this.$msg.textContent = t.error_add;
        this.$btn.disabled = false; this.$btn.textContent = t.add_to_cart;
      }
    }
  }
  if (!customElements.get('pr-booking')) customElements.define('pr-booking', PrBooking);
})();
